var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as yup from 'yup';
import { PolicyContactAddressField, PolicyContactAddressOperator, PolicyAmountsTaxLineType, PolicyCalculationType, PolicyEscalationType, PolicyDayPeriodType, PolicyIncludeExcludeType, PolicyIntervalType, PolicyProcessType, PolicyState, PolicyStatementInterestTo, PolicyStatementType, PolicyTargetStatus, PolicyType, StatementRelativePeriods, } from '../../models';
// All policies need:
// a title
// a policy type
// org id and deleted flag
// start/end dates
// a minimum threshold
const baseSchema = yup.object().shape({
    policy_type: yup.string().required().oneOf(Object.values(PolicyType)),
    state: yup.string().required().oneOf(Object.values(PolicyState)),
    title: yup.string().required('Title is required'),
    deleted: yup.boolean().optional().nullable(),
    organisation_id: yup.string().required(),
    apply_before_due_date_string: yup.string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in the format YYYY-MM-DD')
        .optional()
        .nullable()
        .transform(v => (v ? v : null))
        .when('apply_after_due_date_string', (apply_after_due_date_string, schema) => {
        if (apply_after_due_date_string[0]) {
            return schema.test({
                name: 'apply_before_after_relative_test',
                message: 'Apply to invoices due until date must be after apply to invoices due from date.',
                test: function (value) {
                    return new Date(value).getTime() > new Date(apply_after_due_date_string[0]).getTime() || !value;
                },
            });
        }
        return schema;
    }),
    apply_after_due_date_string: yup.string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in the format YYYY-MM-DD')
        .optional()
        .nullable()
        .transform(v => (v ? v : null)),
    minimum_threshold: yup.number().min(0, 'Minimum threshold must not be less than 0').nullable()
        .transform(v => (isNaN(v) ? 0 : v)),
    maximum_threshold: yup.number().min(0, 'Maximum threshold must not be less than 0').max(2147483640, `Maximum value is 21,474,836`).nullable()
        .transform(v => (isNaN(v) ? 0 : v))
        .when('minimum_threshold', (minimum_threshold, schema) => {
        if (minimum_threshold) {
            return schema.test('is-greater-than-min-or-zero', 'Maximum Due Amount must be greater than Minimum Due Amount', function (value) {
                return value >= minimum_threshold[0] || !value;
            });
        }
        return schema;
    }),
    exclude_branding_themes: yup.string().nullable(),
    hour_of_day: yup.number().integer().min(0).max(23).optional().nullable(),
    system: yup.boolean().nullable().optional(),
    contact_tags: yup.string().nullable(),
    contact_tags_exclude_include: yup.string().oneOf(Object.values(PolicyIncludeExcludeType)).nullable(),
    contact_address_filter_field: yup.string().oneOf(Object.values(PolicyContactAddressField)).nullable(),
    contact_address_filter_operator: yup.string().oneOf(Object.values(PolicyContactAddressOperator)).nullable(),
    contact_address_filter_value: yup.string()
        .transform(v => (typeof v === 'string' ? v.trim() : v) || null)
        .nullable(),
    xero_group_name: yup.string().nullable(),
    xero_group_name_exclude_include: yup.string().oneOf(Object.values(PolicyIncludeExcludeType)).nullable(),
    email_sender_profile_id: yup.number().nullable().optional(),
    webhook_disabled: yup.boolean().nullable().optional(),
    skip_when_payment_promised: yup.boolean().optional(),
    skip_when_query_open: yup.boolean().optional(),
});
const reminderTypeBase = baseSchema.concat(yup.object().shape({
    target_status: yup.string().oneOf(Object.values(PolicyTargetStatus)).required(`You must select a valid target status`),
    day_index: yup.number().integer(`Enter whole numbers only`)
        .when(`target_status`, ([targetStatus], schema) => {
        switch (targetStatus) {
            case PolicyTargetStatus.DUE:
                return schema
                    .min(-90, 'Maximum is 90 days before due date')
                    .max(365, 'Maximum is 365 days after due date');
            case PolicyTargetStatus.MARKED_PAID:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Marked Paid"')
                    .max(90, 'Maximum is 90 days after paid date');
            case PolicyTargetStatus.ISSUED:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Issued"')
                    .max(365, 'Maximum is 365 days after issued date');
            case PolicyTargetStatus.INVOICE_AMOUNT_UPDATED:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Amount Updated"')
                    .max(90, 'Maximum is 90 days after the amount changed');
            case PolicyTargetStatus.PROMISE_BROKEN:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Promise Broken"')
                    .max(90, 'Maximum is 90 days after the promise was broken');
            case PolicyTargetStatus.PROMISE_DUE:
                return schema
                    .min(-30, 'Maximum is 30 days before the promised payment date')
                    .max(0, 'Use "Before" or "On" with "Promised Payment Date". Use "Promise Broken" to follow up after a missed promise');
        }
    })
        .typeError('Days can only be a number'),
    reference_contains: yup.string().max(255, `Reference is limited to 255 characters`).nullable().optional(),
    day_period_type: yup.string().oneOf(Object.values(PolicyDayPeriodType)).required(`You must select a day period type`),
    only_send_to_primary_contact: yup.boolean().nullable().optional(),
}));
const lateFeeSchema = baseSchema.concat(yup.object().shape({
    calculation_type: yup.string().oneOf(['fixed', 'percent', 'prorata']).required(),
    fixed_fee_amount: yup.number().when('calculation_type', {
        is: val => val === 'fixed',
        then: s => s.required().min(0, `Amount cannot be less than 0`).typeError('Amount can only be a number'),
        otherwise: s => s.strip(),
    }),
    percent: yup.number().when('calculation_type', {
        is: val => ['percent', 'prorata'].includes(val),
        then: s => s.required().typeError('Percent can only be a number'),
        otherwise: s => s.strip(),
    }),
    grace_period: yup.number().integer(`Enter whole numbers only`)
        .when(`calculation_type`, {
        is: v => v === PolicyCalculationType.PRORATA,
        then: s => s.min(1, `Grace period must be at least 1 day when using Prorata mode`),
        otherwise: s => s.min(0, `Grace period must not be less than 0`),
    }).required().typeError('Days can only be a number'),
    invoice_status: yup.string().when(['process_type', 'on_update_fail_send_new'], {
        is: ([processType, onUpdateFailSendNew]) => processType === PolicyProcessType.NEW_INVOICE || onUpdateFailSendNew,
        then: s => s.required().oneOf(['DRAFT', 'AUTHORISED']),
        otherwise: s => s.strip(),
    }),
    line_item_text: yup.string().max(3900).required(),
    process_type: yup.string().required().oneOf([PolicyProcessType.NEW_INVOICE, PolicyProcessType.UPDATE_INVOICE]),
    on_update_fail_send_new: yup.boolean().required().default(false),
    repeating: yup.boolean().required().default(false),
    repeat_frequency: yup.number().integer('Enter whole numbers only').when('repeating', {
        is: val => !!val,
        then: s => s.min(1, 'Repeat frequency must be at least 1 day').required('Repeat frequency is required when repeat is enabled').typeError('Days can only be a number'),
        otherwise: s => s.strip(),
    }),
    use_contact_tax_rate: yup.boolean().required().default(false),
    xero_account_code: yup.string().required('Account code is missing'),
    tax_rate: yup.string().optional().nullable(),
    penalty_due_date: yup.number().max(7, `Penalty due date must 1 week or less`).min(0, `Penalty due date must be Original or Penalty Date`).optional().nullable(),
    day_period_type: yup.string().oneOf(Object.values(PolicyDayPeriodType)).required(`You must select a grace period type`),
    exclude_account_lines: yup.string().nullable(),
    use_central_interest_rate: yup.boolean().default(false),
    invoice_reference: yup.string().max(255, `Invoice reference is limited to 200 characters`).required(`Invoice reference is required`),
    use_invoice_number_prefix: yup.boolean().default(false).optional().nullable(),
    new_invoice_branding_theme_id: yup.string().nullable().optional(),
    tracking_category_class: yup.string().nullable().optional(),
    accounting_system_location: yup.string().nullable().optional(),
    template_id: yup.string().nullable(),
    do_not_send_email_to_customer: yup.boolean().default(false).optional().nullable(),
    reference_contains: yup.string().max(255, `Reference is limited to 255 characters`).nullable().optional(),
    first_time_only: yup.boolean().default(false).optional().nullable(),
    target_status: yup.string().oneOf([PolicyTargetStatus.DUE, PolicyTargetStatus.ISSUED]).nullable().optional(),
    earliest_interest_date_string: yup.string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in the format YYYY-MM-DD')
        .optional()
        .nullable()
        .transform(v => (v ? v : null)),
}));
const intervalLateFeeSchema = baseSchema.concat(yup.object().shape({
    calculation_type: yup.string().oneOf(['percent', 'prorata', 'not_applicable']).required(),
    fixed_fee_amount: yup.number().min(0, `Amount cannot be less than 0`),
    percent: yup.number().when('fixed_fee_amount', {
        is: val => !val,
        then: s => s.min(0.01, `Percent and Fixed amounts cannot both be 0`).required().typeError('Percent can only be a number'),
    }),
    day_index: yup.number().integer(`Enter whole numbers only`).when('interval_type', {
        is: val => val === PolicyIntervalType.MONTHLY || val === PolicyIntervalType.MONTHLY_WEEKDAYS,
        then: s => s.min(1, 'Amount must be between 1 and 31 days').max(31, `Amount must be between 1 and 31 days`),
        otherwise: s => s.min(1, 'Amount must be a valid weekday').max(7, `Amount must be a valid weekday`),
    }).required().typeError('Days can only be a number'),
    interval_type: yup.string().oneOf(Object.values(PolicyIntervalType)).required(),
    invoice_status: yup.string().required().oneOf(['DRAFT', 'AUTHORISED']),
    invoice_reference: yup.string().max(255, `Invoice reference is limited to 200 characters`).nullable(),
    fixed_amount_line_description: yup.string().max(3900).nullable(),
    fixed_amount_account_code: yup.string().nullable(),
    fixed_amount_tax_rate: yup.string().nullable(),
    overdue_age_min: yup.number().integer(`Enter whole numbers only`)
        .when(`calculation_type`, {
        is: v => v === PolicyCalculationType.PRORATA,
        then: s => s.min(1, `Minimum overdue age must be at least 1 day when using Prorata mode`),
        otherwise: s => s.min(0, `Minimum overdue age must not be less than 0`),
    }).required().typeError('Days can only be a number'),
    overdue_age_max: yup.number().integer(`Enter whole numbers only`).min(0, 'Maximum overdue age must not be less than 0').nullable().typeError('Days can only be a number').when(`overdue_age_min`, (overdue_age_min, schema) => {
        if (overdue_age_min) {
            return schema.test('is-greater-than-min-or-zero', 'Maximum overdue age must be greater than Minimum overdue age', function (value) {
                return value >= overdue_age_min[0] || !value;
            });
        }
        return schema;
    }),
    amounts_tax_line_type: yup.string().oneOf(Object.values(PolicyAmountsTaxLineType)).nullable(),
    // line_item_text: yup.string().required(),
    xero_account_code: yup.string().required('Account code is missing'),
    adjust_for_credit: yup.boolean().required().default(false),
    tax_rate: yup.string().optional().nullable(),
    penalty_due_date: yup.number().integer(`Enter whole numbers only`).min(0, 'Must be due on or after fee is issued').optional().nullable().typeError('can only be a number').default(0),
    statement_type: yup.string().oneOf(Object.values(PolicyStatementType)).required(),
    template_id: yup.string().nullable(),
    min_fee_amount: yup.number().min(0.00, `Minimum fee amount must not be negative`).nullable().typeError('Amount can only be a number'),
    use_central_interest_rate: yup.boolean().default(false),
    use_invoice_number_prefix: yup.boolean().default(false).optional().nullable(),
    reference_contains: yup.string().max(255, `Reference is limited to 255 characters`).nullable().optional(),
    include_previous_interest: yup.boolean().default(true),
    new_invoice_branding_theme_id: yup.string().nullable().optional(),
    statement_interest_to: yup.string().oneOf(Object.values(PolicyStatementInterestTo)).nullable().optional(),
    parent_only: yup.boolean().default(false).optional().nullable(),
    exclude_account_lines: yup.string().nullable(),
    tracking_category_class: yup.string().nullable().optional(),
    accounting_system_location: yup.string().nullable().optional(),
    earliest_interest_date_string: yup.string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in the format YYYY-MM-DD')
        .optional()
        .nullable()
        .transform(v => (v ? v : null)),
}));
const reminderSchema = baseSchema.concat(reminderTypeBase).concat(yup.object({
    reminder_platform: yup.string().oneOf(['email']).required('You must select a platform'),
    ai_prompt: yup.string().nullable().optional()
        .max(4000, `Prompt must be 4000 characters or less`)
        .test('not-blank', `Enter a prompt for the AI`, v => v === null || v === undefined || v.trim().length > 0),
    template_id: yup.string().nullable().when('ai_prompt', {
        is: (prompt) => prompt !== null && prompt !== undefined,
        then: s => s.optional(),
        otherwise: s => s.required(`You must select a template`),
    }),
    include_invoice_pdf: yup.boolean().nullable().default(false),
    attach_ics: yup.boolean().nullable().default(false),
    include_attachments: yup.boolean().nullable().default(false),
    exclude_account_lines: yup.string().nullable(),
    mark_as_sent: yup.boolean().nullable().default(false),
    destination_email_override: yup.string().email().optional().nullable(),
}));
const discountSchema = baseSchema.concat(yup.object({
    calculation_type: yup.string().oneOf([PolicyCalculationType.FIXED, PolicyCalculationType.PERCENT]).required(),
    fixed_fee_amount: yup.number().when('calculation_type', {
        is: val => val === PolicyCalculationType.FIXED,
        then: s => s.required().typeError('Amount can only be a number'),
        otherwise: s => s.strip(),
    }),
    percent: yup.number().when('calculation_type', {
        is: val => val === PolicyCalculationType.PERCENT,
        then: s => s.required().typeError('Percent can only be a number'),
        otherwise: s => s.strip(),
    }),
    discount_applies: yup.string().oneOf(['new', 'unsent'], 'Select new or unsent invoices to apply the discount to').required('Select when to apply the discount to invoices'),
    line_item_text: yup.string().max(255, `Description is limited to 255 characters`).required(),
    grace_period: yup.number().integer(`Enter whole numbers only`).required()
        .when(`discount_removal_target_status`, ([removalTargetStatus], schema) => {
        switch (removalTargetStatus) {
            case PolicyTargetStatus.DUE:
                return schema;
            case PolicyTargetStatus.ISSUED:
                return schema
                    .max(1, 'You must use "After" when selecting "Issued" with a minimum of 1 day');
        }
    })
        .typeError('Days can only be a number'),
    use_contact_tax_rate: yup.boolean().required().default(false),
    xero_account_code: yup.string().required('Account code is missing'),
    tax_rate: yup.string().optional().nullable(),
    template_id: yup.string().nullable().optional(),
    target_status: yup.string().oneOf([PolicyTargetStatus.DUE, PolicyTargetStatus.ISSUED]).required(`You must select a valid target status`),
    discount_removal_target_status: yup.string().oneOf([PolicyTargetStatus.DUE, PolicyTargetStatus.ISSUED]).required(`You must select a valid target status for discount removal`),
    day_index: yup.number().integer(`Enter whole numbers only`)
        .when(`target_status`, ([targetStatus], schema) => {
        switch (targetStatus) {
            case PolicyTargetStatus.DUE:
                return schema
                    .min(-90, 'Maximum is 90 days before due date')
                    .max(0, 'Maximum is on the due date');
            case PolicyTargetStatus.ISSUED:
                return schema
                    .min(-30, 'At most 30 days before issued date')
                    .max(30, 'Maximum is 30 days after issued date');
        }
    })
        .typeError('Days can only be a number'),
    exclude_account_lines: yup.string().nullable(),
    reference_contains: yup.string().max(255, `Reference is limited to 255 characters`).nullable().optional(),
    mark_as_sent: yup.boolean().nullable().default(false),
}));
const smsSchema = baseSchema.concat(reminderTypeBase).concat(yup.object({
    sms_content: yup.string().max(320, `SMS Content is limited to 320 characters`).min(10, `SMS Content must be at least 10 characters long`).required('SMS content is required'), //TODO: max chars after merge tags??
    only_webhook: yup.boolean().nullable().optional(),
    sms_fallback_to_phone: yup.boolean().nullable().optional(),
    sms_default_country_code: yup.string().nullable().optional(),
    exclude_account_lines: yup.string().nullable(),
}));
const quoteEmailSchema = baseSchema.concat(yup.object({
    target_status: yup.string().oneOf([PolicyTargetStatus.EXPIRY_DATE, PolicyTargetStatus.QUOTE_ISSUE_DATE]).required(`You must select a valid target status`),
    day_index: yup.number().integer(`Enter whole numbers only`)
        .when(`target_status`, ([targetStatus], schema) => {
        switch (targetStatus) {
            case PolicyTargetStatus.EXPIRY_DATE:
                return schema
                    .min(-90, `Cannot send email more than 90 days before expiry`)
                    .max(365, `Cannot send email more than 365 days after expiry`);
            case PolicyTargetStatus.QUOTE_ISSUE_DATE:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Issue Date"')
                    .max(365, 'Maximum is 365 days after issued date');
        }
    }).integer(`Enter whole numbers only`),
    template_id: yup.string().required(`You must select an email template`),
    destination_email_override: yup.string().email().optional().nullable(),
}));
const statementSchema = baseSchema.concat(yup.object({
    only_send_to_primary_contact: yup.boolean().nullable().optional(),
    day_index: yup.number().integer(`Enter whole numbers only`).when('interval_type', {
        is: val => val === PolicyIntervalType.MONTHLY || val === PolicyIntervalType.MONTHLY_WEEKDAYS,
        then: s => s.min(1, 'Amount must be between 1 and 31 days').max(31, `Amount must be between 1 and 31 days`),
        otherwise: s => s.min(1, 'Amount must be a valid weekday').max(7, `Amount must be a valid weekday`),
    }).required().typeError('Days can only be a number'),
    interval_type: yup.string().oneOf(Object.values(PolicyIntervalType)).required(),
    template_id: yup.string().required(`You must select a template`),
    statement_type: yup.string().oneOf(Object.values(PolicyStatementType)).required(),
    minimum_threshold: yup.number().min(0, 'Minimum threshold must not be less than 0').nullable()
        .transform(v => (isNaN(v) ? 0 : v)),
    statement_period: yup.string().oneOf(Object.values(StatementRelativePeriods)).required(),
    overdue_age_min: yup.number().integer(`Enter whole numbers only`).min(0, 'Minimum overdue age must not be less than 0').nullable().optional().typeError('Days can only be a number'),
    overdue_age_max: yup.number().integer(`Enter whole numbers only`).min(0, 'Maximum overdue age must not be less than 0').nullable().typeError('Days can only be a number')
        .when(`overdue_age_min`, (overdue_age_min, schema) => {
        if (overdue_age_min) {
            return schema.test('is-greater-than-min-or-zero', 'Maximum overdue age must be greater than Minimum overdue age', function (value) {
                return value >= overdue_age_min[0] || !value;
            });
        }
        return schema;
    }),
    destination_email_override: yup.string().email().optional().nullable(),
    parent_only: yup.boolean().default(false).optional().nullable(),
    include_statement_pdf: yup.boolean().nullable().default(true),
    include_activity_pdf: yup.boolean().nullable().default(false),
    statement_template_id: yup.number().integer().nullable().default(null),
    use_standard_statement: yup.boolean().default(false),
}));
/** Up to 5 comma separated emails to send the "escalation raised" email to */
export const escalationEmailsSchema = yup.string().optional().nullable().test('is-comma-sep-emails', () => `email addresses is not a comma separated list of emails`, value => value == null || value.split(',').every((email) => yup.string().email().isValidSync(email.trim()))).test('max-5-emails', () => `Maximum of 5 emails`, value => value == null || value.split(',').length <= 5);
/** Body for manually raising an escalation */
export const createEscalationSchema = yup.object({
    contactId: yup.number().integer().required(),
    invoiceId: yup.string().uuid().optional().nullable(),
    type: yup.string().oneOf(Object.values(PolicyEscalationType)).required(),
    steps: yup.string().optional().nullable(),
    assignedUserId: yup.string().uuid().optional().nullable(),
    assignToRecordAssignee: yup.boolean().optional().nullable(),
    notifyEmails: escalationEmailsSchema,
});
const escalationSchemaBase = baseSchema.concat(yup.object({
    day_period_type: yup.string().oneOf(Object.values(PolicyDayPeriodType)).required(`You must select a day period type`),
    escalation_type: yup.string().required().default(PolicyEscalationType.CALL),
    escalation_steps: yup.string().optional().nullable(),
    escalation_emails: escalationEmailsSchema,
    notes: yup.string().optional().nullable(),
    exclude_account_lines: yup.string().nullable(),
    reference_contains: yup.string().max(255, `Reference is limited to 255 characters`).nullable().optional(),
    /** When creating an escalation, the default assignee user */
    assignee_user_id: yup.string().nullable(),
    assign_to_record_assignee: yup.boolean().nullable(),
    minimum_threshold: yup.number().min(0, 'Minimum threshold must not be less than 0').nullable().optional(),
}));
const escalationSchema = escalationSchemaBase.concat(yup.object({
    target_status: yup.string().oneOf([PolicyTargetStatus.DUE, PolicyTargetStatus.ISSUED, PolicyTargetStatus.PROMISE_BROKEN, PolicyTargetStatus.PROMISE_DUE]).required(`Only due date, issue date and payment promise triggers are currently supported`),
    day_index: yup.number().integer(`Enter whole numbers only`)
        .when(`target_status`, ([targetStatus], schema) => {
        switch (targetStatus) {
            case PolicyTargetStatus.DUE:
                return schema
                    .min(-90, 'Maximum is 90 days before due date')
                    .max(365, 'Maximum is 365 days after due date');
            case PolicyTargetStatus.ISSUED:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Issued"')
                    .max(365, 'Maximum is 365 days after issued date');
            case PolicyTargetStatus.PROMISE_BROKEN:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Promise Broken"')
                    .max(90, 'Maximum is 90 days after the promise was broken');
            case PolicyTargetStatus.PROMISE_DUE:
                return schema
                    .min(-30, 'Maximum is 30 days before the promised payment date')
                    .max(0, 'Use "Before" or "On" with "Promised Payment Date". Use "Promise Broken" to follow up after a missed promise');
        }
    })
        .typeError('Days can only be a number'),
    exclude_account_lines: yup.string().nullable(),
    reference_contains: yup.string().max(255, `Reference is limited to 255 characters`).nullable().optional(),
}));
const escalationContactSchema = escalationSchemaBase.concat(yup.object({
    target_status: yup.string().oneOf([PolicyTargetStatus.OLDEST_DUE]).required(`Only oldest due date trigger is currently supported`), // TODO add more triggers
    maximum_threshold: yup.number().min(0, 'Maximum threshold must not be less than 0').nullable(),
    day_index: yup.number().integer(`Enter whole numbers only`)
        .when(`target_status`, ([targetStatus], schema) => {
        switch (targetStatus) {
            case PolicyTargetStatus.OLDEST_DUE:
                return schema
                    .min(-90, 'Maximum is 90 days before due date')
                    .max(365, 'Maximum is 365 days after due date');
            default:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Issued"')
                    .max(365, 'Maximum is 365 days after issued date');
        }
    })
        .typeError('Days can only be a number'),
}));
const paymentPlanReminderSchema = baseSchema.concat(yup.object({
    target_status: yup.string().oneOf([PolicyTargetStatus.INSTALMENT_DUE_DATE]).required(`Only installment due date trigger is currently supported`),
    day_index: yup.number().integer(`Enter whole numbers only`)
        .when(`target_status`, ([targetStatus], schema) => {
        switch (targetStatus) {
            case PolicyTargetStatus.INSTALMENT_DUE_DATE:
                return schema
                    .min(-90, 'Maximum is 90 days before due date')
                    .max(365, 'Maximum is 365 days after due date');
            default:
                return schema
                    .min(0, 'You must use "On" or "After" when selecting "Created"')
                    .max(365, 'Maximum is 365 days after issued date');
        }
    })
        .typeError('Days can only be a number'),
    template_id: yup.string().required(`You must select a template`),
    include_invoice_pdf: yup.boolean().nullable().default(false),
    attach_ics: yup.boolean().nullable().default(false),
}));
const onDemandPlaceholderPolicySchema = baseSchema.concat(yup.object({}));
// TODO: make type safe
export function validatePolicy(policy_1) {
    return __awaiter(this, arguments, void 0, function* (policy, abortEarly = true) {
        if (policy === null || policy === void 0 ? void 0 : policy.policy_type) {
            switch (policy.policy_type) {
                case PolicyType.LATE_FEE:
                    return yield validateLateFeePolicy(policy, abortEarly);
                case PolicyType.STATEMENT_LATE_FEE:
                    return yield validateIntervalLateFeePolicy(policy, abortEarly);
                case PolicyType.DISCOUNT:
                    return yield validateDiscountPolicy(policy, abortEarly);
                case PolicyType.REMINDER:
                    return yield validateReminderPolicy(policy, abortEarly);
                case PolicyType.SMS:
                    return yield validateSmsPolicy(policy, abortEarly);
                case PolicyType.STATEMENT:
                    return yield validateStatementPolicy(policy, abortEarly);
                case PolicyType.ESCALATION:
                    return yield validateEscalationPolicy(policy, abortEarly);
                case PolicyType.CONTACT_ESCALATION:
                    return yield validateContactEscalationPolicy(policy, abortEarly);
                case PolicyType.QUOTE_EMAIL:
                    return yield validateQuoteEmailPolicy(policy, abortEarly);
                case PolicyType.PAYMENT_PLAN_REMINDER:
                    return yield validatePaymentPlanReminderPolicy(policy, abortEarly);
                case PolicyType.ON_DEMAND_PLACEHOLDER:
                    return yield validateOnDemandPlaceholderPolicy(policy, abortEarly);
                default:
                    throw new Error('Policy type not found');
            }
        }
        else {
            throw new Error('Policy and Policy type is required to validate');
        }
    });
}
export function validatePolicySync(policy, abortEarly = true) {
    if (policy === null || policy === void 0 ? void 0 : policy.policy_type) {
        switch (policy.policy_type) {
            case PolicyType.LATE_FEE:
                return validateLateFeePolicySync(policy, abortEarly);
            case PolicyType.STATEMENT_LATE_FEE:
                return validateIntervalLateFeePolicySync(policy, abortEarly);
            case PolicyType.DISCOUNT:
                return validateDiscountPolicySync(policy, abortEarly);
            case PolicyType.REMINDER:
                return validateReminderPolicySync(policy, abortEarly);
            case PolicyType.SMS:
                return validateSmsPolicySync(policy, abortEarly);
            case PolicyType.STATEMENT:
                return validateStatementPolicySync(policy, abortEarly);
            case PolicyType.ESCALATION:
                return validateEscalationPolicySync(policy, abortEarly);
            case PolicyType.CONTACT_ESCALATION:
                return validateContactEscalationPolicySync(policy, abortEarly);
            case PolicyType.QUOTE_EMAIL:
                return validateQuoteEmailPolicySync(policy, abortEarly);
            case PolicyType.PAYMENT_PLAN_REMINDER:
                return validatePaymentPlanReminderPolicySync(policy, abortEarly);
            case PolicyType.ON_DEMAND_PLACEHOLDER:
                return validateOnDemandPlaceholderPolicySync(policy, abortEarly);
            default:
                throw new Error('Policy type not found');
        }
    }
    else {
        throw new Error('Policy and Policy type is required to validate');
    }
}
function validateLateFeePolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield lateFeeSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateIntervalLateFeePolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield intervalLateFeeSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateDiscountPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield discountSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateReminderPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield reminderSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateSmsPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield smsSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateStatementPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield statementSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateEscalationPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return escalationSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateContactEscalationPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return escalationContactSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateQuoteEmailPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return quoteEmailSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validatePaymentPlanReminderPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return paymentPlanReminderSchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
function validateOnDemandPlaceholderPolicy(policy, abortEarly) {
    return __awaiter(this, void 0, void 0, function* () {
        return onDemandPlaceholderPolicySchema.noUnknown().validate(policy, {
            strict: false, // strips unknowns
            abortEarly,
        });
    });
}
// Sync versions
function validateStatementPolicySync(policy, abortEarly) {
    return statementSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateEscalationPolicySync(policy, abortEarly) {
    return escalationSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateContactEscalationPolicySync(policy, abortEarly) {
    return escalationContactSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateLateFeePolicySync(policy, abortEarly) {
    return lateFeeSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateIntervalLateFeePolicySync(policy, abortEarly) {
    return intervalLateFeeSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateDiscountPolicySync(policy, abortEarly) {
    return discountSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateReminderPolicySync(policy, abortEarly) {
    return reminderSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateSmsPolicySync(policy, abortEarly) {
    return smsSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateQuoteEmailPolicySync(policy, abortEarly) {
    return quoteEmailSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validatePaymentPlanReminderPolicySync(policy, abortEarly) {
    return paymentPlanReminderSchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
function validateOnDemandPlaceholderPolicySync(policy, abortEarly) {
    return onDemandPlaceholderPolicySchema.noUnknown().validateSync(policy, {
        strict: false, // strips unknowns
        abortEarly,
    });
}
//# sourceMappingURL=policy.js.map